import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { CartItem } from './cart-item'
import {
  useCartStore,
  useCartItems,
  useCartIsOpen,
  calculateCartTotals,
} from '@/stores/cart-store'
import { supabase } from '@/lib/supabase'
import type { Product } from '@/types/database'
import {
  callEdgeFunction,
  type EdgeFunctionErrorCode,
} from '@/lib/edge-functions'
import { toast } from 'sonner'

function getCheckoutErrorMessage(
  errorCode: EdgeFunctionErrorCode | null,
  fallbackError: string | null
): string {
  if (errorCode === 'network_error') {
    return 'Network issue while starting checkout. Please try again.'
  }

  if (errorCode === 'session_expired') {
    return 'Your session expired and checkout could not be started. Please sign in again.'
  }

  if (errorCode === 'unauthorized') {
    return 'Checkout authorization failed. Please refresh and try again.'
  }

  return fallbackError || 'Failed to start checkout. Please try again.'
}

export function CartDrawer() {
  const isOpen = useCartIsOpen()
  const closeCart = useCartStore(state => state.closeCart)
  const cartItems = useCartItems()

  const [products, setProducts] = useState<Map<string, Product>>(new Map())
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)

  // Fetch product details when cart items change
  useEffect(() => {
    if (cartItems.length === 0) {
      setProducts(new Map())
      return
    }

    const productIds = cartItems.map(item => item.productId)

    // Only fetch if we don't have all products cached
    const missingIds = productIds.filter(id => !products.has(id))
    if (missingIds.length === 0) return

    setIsLoading(true)

    supabase
      .from('products')
      .select('*')
      .in('id', missingIds)
      .in('status', ['featured', 'archived'])
      .then(({ data, error }) => {
        if (error) {
          console.error('Failed to fetch products:', error)
        } else if (data) {
          setProducts(prev => {
            const newMap = new Map(prev)
            data.forEach(p => newMap.set(p.id, p as Product))
            return newMap
          })
        }
        setIsLoading(false)
      })
  }, [cartItems])

  // Calculate totals
  const { subtotal, totalSavings, itemCount } = calculateCartTotals(cartItems, products)

  // Handle checkout
  const handleCheckout = async () => {
    if (cartItems.length === 0) return

    setIsCheckingOut(true)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const result = await callEdgeFunction<{ url: string }>('create-checkout-session', {
        method: 'POST',
        withAuth: true,
        body: {
          origin: window.location.origin,
          customerEmailHint: session?.user?.email,
          items: cartItems.map(item => ({
            productId: item.productId,
            variantId: item.variantId,
            quantity: item.quantity,
            customInputs: item.customInputs,
          })),
        },
      })

      if (result.error || !result.data?.url) {
        console.error('Checkout error:', {
          error: result.error,
          errorCode: result.errorCode,
          status: result.status,
        })
        toast.error(getCheckoutErrorMessage(result.errorCode, result.error))
        return
      }

      closeCart()
      // Redirect to Stripe Checkout
      window.location.href = result.data.url
    } catch (err) {
      console.error('Checkout error:', err)
      toast.error('Failed to start checkout. Please try again.')
    } finally {
      setIsCheckingOut(false)
    }
  }

  /** Composite key for rendering unique cart items */
  const itemKey = (item: { productId: string; variantId: string | null }) =>
    item.variantId ? `${item.productId}::${item.variantId}` : item.productId

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && closeCart()}>
      <SheetContent
        side="right"
        className="flex w-[90vw] max-w-md flex-col bg-white"
      >
        <SheetHeader className="border-b border-zinc-200 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart className="size-5" />
            Your Cart
            {itemCount > 0 && (
              <span className="ml-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                {itemCount}
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Review cart items and continue to checkout
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="size-8 animate-spin text-zinc-400" />
          </div>
        ) : cartItems.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <ShoppingCart className="size-16 text-zinc-300" />
            <div>
              <p className="text-lg font-medium text-zinc-600">Your cart is empty</p>
              <p className="text-sm text-zinc-500">
                Add some products to get started!
              </p>
            </div>
            <Button
              variant="outline"
              onClick={closeCart}
              className="mt-2"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-4 px-4">
              <div className="divide-y divide-zinc-200">
                {cartItems.map(item => {
                  const product = products.get(item.productId)
                  if (!product) return null
                  return (
                    <CartItem
                      key={itemKey(item)}
                      item={item}
                      product={product}
                    />
                  )
                })}
              </div>
            </ScrollArea>

            <SheetFooter className="flex-col gap-4 border-t border-zinc-200 pt-4">
              {/* Totals */}
              <div className="w-full space-y-2">
                {totalSavings > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">You're saving</span>
                    <span className="font-medium text-green-600">
                      ${totalSavings.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold">
                  <span className="text-zinc-900">Subtotal</span>
                  <span className="text-zinc-900">${subtotal.toFixed(2)}</span>
                </div>
                <p className="text-xs text-zinc-500">
                  Shipping calculated at checkout
                </p>
              </div>

              <Separator />

              {/* Actions */}
              <div className="w-full space-y-2">
                <Button
                  className="w-full bg-red-600 text-white hover:bg-red-700"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={isCheckingOut || cartItems.length === 0}
                >
                  {isCheckingOut ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>Checkout - ${subtotal.toFixed(2)}</>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  className="w-full text-zinc-600 hover:text-zinc-900"
                  onClick={closeCart}
                  asChild
                >
                  <Link to="/cart">View Full Cart</Link>
                </Button>
              </div>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
